import {useSelector} from '@xstate/react';
import React, {useEffect, useMemo, useRef} from 'react';
import {ActorRefFrom} from 'xstate';

import ParametersForPlateMapWrapper from 'notebook/integrations/procedures/ParametersForPlateMapWrapper';
import {PlateDataParameterContext} from 'samples/plate-maps/hooks/usePlateDataParameterContext';
import {
  PLATE_MAP_MAX_DATA_PARAMETER_LAYERS,
  ParameterFieldType,
  PlateDataParameter,
} from 'samples/plate-maps/PlateMapConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import DefineScratchPlateMapStep, {DefineScratchPlateMapStepProps} from './DefineScratchPlateMapStep';
import ScratchPlateConcentrationLayerProvider from './ScratchPlateConcentrationLayerProvider';
import ScratchPlateMetadataLayerProvider from './ScratchPlateMetadataLayerProvider';
import {ScratchPlateMachine} from './ScratchPlateModalMachine';
import ScratchPlateParameterLayerProvider from './ScratchPlateParameterLayerProvider';

export interface ConnectedDefineScratchPlateMapStepProps {
  plateActor: ActorRefFrom<typeof ScratchPlateMachine>;
  wellSchemaLayers: DefineScratchPlateMapStepProps['wellSchemaLayers'];
  maxPlateViewHeight: string;
  /**
   * Called when an edit invalidates previously-resolved transfer contents — i.e. plate-map or
   * concentration-layer changes. Metadata-layer edits intentionally do not invalidate contents
   * since they don't affect fill/transfer results.
   */
  onInvalidateContents?: () => void;
  /**
   * Whether to pre-populate the Parameters section with every available procedure parameter the
   * first time this renders with a plate map. Only meaningful for new designs — the caller is
   * responsible for gating this off once editing an existing design (which may deliberately have
   * fewer parameter layers than are available) and for not re-seeding after the first attempt
   * (e.g. when the user steps back and forward between modal steps).
   */
  shouldSeedParameterLayers?: boolean;
  /** Called once seeding has been attempted (whether or not there was anything to add). */
  onParameterLayersSeeded?: () => void;
}

/** Maps a procedure data parameter's schema field type to the scratch-plate parameter field type. */
export function dataParameterFieldType(parameter: PlateDataParameter): ParameterFieldType {
  switch (parameter.schemaField.fieldType) {
    case FieldType.FLOAT:
      return ParameterFieldType.FLOAT;
    case FieldType.DATETIME:
      return ParameterFieldType.DATETIME;
    default:
      return ParameterFieldType.TEXT;
  }
}

/**
 * Wires {@link DefineScratchPlateMapStep} to a {@link ScratchPlateMachine} actor: it sources plate
 * state from the actor, provides the concentration/metadata/parameter layer actors via context, and
 * forwards each layer/schema-field action as the corresponding machine event. This keeps the
 * create-records and design modals from duplicating the (verbose) wiring.
 *
 * Procedure association: this wraps the step in {@link ParametersForPlateMapWrapper}, which resolves
 * the worksheet/procedure-step context (via WorksheetContext) and supplies the procedure's
 * plate-map data parameters. Outside a procedure it gracefully yields no parameters, so the
 * Parameters section is simply empty. This mirrors how the legacy plate map associates with a
 * procedure — no persisted FK; the association is the edit-time worksheet context.
 */
export default function ConnectedDefineScratchPlateMapStep(
  props: ConnectedDefineScratchPlateMapStepProps
): JSX.Element {
  return (
    <ParametersForPlateMapWrapper>
      {({plateDataParameters}) => (
        <ConnectedDefineScratchPlateMapStepInner {...props} plateDataParameters={plateDataParameters} />
      )}
    </ParametersForPlateMapWrapper>
  );
}

function ConnectedDefineScratchPlateMapStepInner({
  plateActor,
  wellSchemaLayers,
  maxPlateViewHeight,
  onInvalidateContents,
  shouldSeedParameterLayers,
  onParameterLayersSeeded,
  plateDataParameters,
}: ConnectedDefineScratchPlateMapStepProps & {
  /** Undefined while {@link ParametersForPlateMapWrapper}'s query is still pending. */
  plateDataParameters: Array<PlateDataParameter> | undefined;
}): JSX.Element {
  const plateState = useSelector(plateActor, (snap) => snap.context);
  const invalidateContents = (): void => onInvalidateContents?.();
  const resolvedPlateDataParameters = useMemo(() => plateDataParameters ?? [], [plateDataParameters]);
  const plateDataParameterContextValue = useMemo(
    () => ({plateDataParameters: resolvedPlateDataParameters}),
    [resolvedPlateDataParameters]
  );

  // Pre-populate the Parameters section on new designs. plateDataParameters is undefined while
  // ParametersForPlateMapWrapper's query is still pending, so that case is excluded below —
  // otherwise the effect would fire immediately with an empty array, flip hasAttemptedSeedRef, and
  // permanently skip seeding once the real data arrives. Guarded by a ref (not just the dependency
  // array) because each addParameterLayer send below produces a new plateMap reference, which would
  // otherwise re-trigger this same effect mid-seed. The caller flips shouldSeedParameterLayers off
  // after the first attempt, so this also doesn't re-run across a remount (e.g. the user stepping
  // back and forward between modal steps).
  const {plateMap} = plateState;
  const hasAttemptedSeedRef = useRef(false);
  useEffect(() => {
    if (
      !shouldSeedParameterLayers ||
      plateMap == null ||
      plateDataParameters == null ||
      hasAttemptedSeedRef.current
    ) {
      return;
    }
    hasAttemptedSeedRef.current = true;
    const existingParameterVersionIds = new Set(Object.keys(plateMap.parameterLayerInfoById ?? {}));
    const remainingCapacity = PLATE_MAP_MAX_DATA_PARAMETER_LAYERS - existingParameterVersionIds.size;
    plateDataParameters
      .filter((parameter) => !existingParameterVersionIds.has(parameter.versionId))
      .slice(0, Math.max(remainingCapacity, 0))
      .forEach((parameter) => {
        plateActor.send({
          type: 'addParameterLayer',
          parameterVersionId: parameter.versionId,
          name: parameter.name,
          fieldType: dataParameterFieldType(parameter),
          unitSymbol: parameter.schemaField.unitSymbol,
        });
      });
    onParameterLayersSeeded?.();
  }, [shouldSeedParameterLayers, plateMap, plateDataParameters, plateActor, onParameterLayersSeeded]);

  return (
    <PlateDataParameterContext.Provider value={plateDataParameterContextValue}>
      <ScratchPlateConcentrationLayerProvider actor={plateState.concentrationLayerMachine!}>
        <ScratchPlateMetadataLayerProvider actor={plateState.metadataLayerMachine!}>
          <ScratchPlateParameterLayerProvider actor={plateState.parameterLayerMachine!}>
            {plateState.plateMap != null && (
              <DefineScratchPlateMapStep
                plateMap={plateState.plateMap}
                setPlateMap={(plateMap) => {
                  plateActor.send({type: 'updatePlateMap', plateMap});
                  invalidateContents();
                }}
                containerSchemaId={plateState.plateSchema?.containerSchemaId ?? null}
                wellSchemaLayers={wellSchemaLayers}
                selectedWellSchemaFieldIds={plateState.selectedWellSchemaFieldIds}
                plateDataParameters={resolvedPlateDataParameters}
                onAddSelectedSchemaField={(schemaFieldId) => {
                  plateActor.send({type: 'addSelectedSchemaFieldId', schemaFieldId});
                }}
                onRemoveSelectedSchemaField={(schemaFieldId) => {
                  plateActor.send({type: 'removeSelectedSchemaFieldId', schemaFieldId});
                }}
                onAddConcentrationLayer={() => {
                  plateActor.send({type: 'addConcentrationLayer'});
                  invalidateContents();
                }}
                onRemoveConcentrationLayer={(layerId) => {
                  plateActor.send({type: 'removeConcentrationLayer', layerId});
                  invalidateContents();
                }}
                onAddMetadataLayer={() => {
                  plateActor.send({type: 'addMetadataLayer'});
                }}
                onRemoveMetadataLayer={(layerId) => {
                  plateActor.send({type: 'removeMetadataLayer', layerId});
                }}
                onUpdateMetadataLayerType={(layerId, layerType) => {
                  plateActor.send({type: 'updateMetadataLayerType', layerId, layerType});
                }}
                onAddParameterLayer={(parameter) => {
                  plateActor.send({
                    type: 'addParameterLayer',
                    parameterVersionId: parameter.versionId,
                    name: parameter.name,
                    fieldType: dataParameterFieldType(parameter),
                    unitSymbol: parameter.schemaField.unitSymbol,
                  });
                }}
                onRemoveParameterLayer={(layerId) => {
                  plateActor.send({type: 'removeParameterLayer', layerId});
                }}
                maxPlateViewHeight={maxPlateViewHeight}
              />
            )}
          </ScratchPlateParameterLayerProvider>
        </ScratchPlateMetadataLayerProvider>
      </ScratchPlateConcentrationLayerProvider>
    </PlateDataParameterContext.Provider>
  );
}
